import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, StickyNote, AlertTriangle, Wrench, Images, RefreshCw } from 'lucide-react';
import api, { API } from '../lib/api';
import { formatCurrency } from '../mock';

const STATE_CLS = { attention: 't-warn', repair: 't-bad' };
const STATE_LB = { attention: 'Atenção', repair: 'Reparo' };

// Laudo da inspeção em modo leitura — reutilizado no Estoque, Deals/F&I e Pós-venda.
export const InspectionReport = ({ vehicleId = '', query = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/inspection-report', { params: { vehicle_id: vehicleId, q: query } });
      setData(d);
    } catch { setData({ found: false }); }
    finally { setLoading(false); }
  }, [vehicleId, query]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="ent-empty" data-testid="insp-report-loading">Carregando laudo da inspeção...</p>;
  if (!data?.found) return <p className="ent-empty" data-testid="insp-report-empty">Nenhuma inspeção registrada para este veículo.</p>;

  const i = data.inspection;
  const photos = i.photos || [];
  const flagged = data.flagged || [];
  const recon = data.recon || [];

  return (
    <div className="space-y-3" data-testid="insp-report">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-border border border-border rounded-sm overflow-hidden">
        <div className="bg-card px-3 py-2"><p className="ent-label">Status</p><p className={`ent-value mt-1 ${i.status === 'Ready' ? 't-ok' : i.status === 'Needs Recon' ? 't-bad' : 't-warn'}`} data-testid="insp-report-status">{i.status}</p></div>
        <div className="bg-card px-3 py-2"><p className="ent-label">Progresso</p><p className="ent-value mt-1">{i.progress?.checked || 0}/{i.progress?.total || 0} · {i.progress?.pct || 0}%</p></div>
        <div className="bg-card px-3 py-2"><p className="ent-label">Itens com problema</p><p className={`ent-value mt-1 ${flagged.length ? 't-warn' : 't-ok'}`}>{flagged.length}</p></div>
        <div className="bg-card px-3 py-2"><p className="ent-label">Recon</p><p className="ent-value mt-1">{recon.length}</p></div>
        <div className="bg-card px-3 py-2"><p className="ent-label">Custo Recon</p><p className="ent-value mt-1">{formatCurrency(data.recon_cost || 0)}</p></div>
        <div className="bg-card px-3 py-2"><p className="ent-label">Técnico</p><p className="ent-value mt-1 truncate">{i.assigned_to_name || i.started_by || '—'}</p></div>
      </div>

      <div className="ent-panel p-3 overflow-hidden">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <p className="ent-label flex items-center gap-1.5"><StickyNote className="w-3.5 h-3.5" /> Observações do técnico</p>
          <div className="flex items-center gap-2">
            {i.notes_by && <span className="text-[11px] t-label">{i.notes_by} · {(i.notes_at || '').slice(0, 10)}</span>}
            <button data-testid="insp-report-reload" onClick={load} className="ent-btn ent-btn-sm"><RefreshCw className="w-3 h-3" /> Atualizar</button>
          </div>
        </div>
        {i.notes ? <p className="text-[13px] leading-relaxed whitespace-pre-wrap" data-testid="insp-report-notes">{i.notes}</p>
          : <p className="text-[12px] t-label">Sem observações registradas.</p>}
      </div>

      {!!flagged.length && (
        <div className="ent-panel overflow-x-auto" data-testid="insp-report-flagged">
          <div className="ent-colhead"><span className="ent-coltitle flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Itens apontados na inspeção</span><span className="ent-pill">{flagged.length}</span></div>
          <table className="ent-table">
            <thead><tr><th>Seção</th><th>Item</th><th>Condição</th><th>Observação</th></tr></thead>
            <tbody>
              {flagged.map((f) => (
                <tr key={f.key}>
                  <td className="t-label">{f.section}</td>
                  <td className="font-medium">{f.label}</td>
                  <td><span className={`ent-tag ${STATE_CLS[f.state] || ''}`}>{STATE_LB[f.state] || f.state}</span></td>
                  <td className="text-foreground/75">{f.note || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!!recon.length && (
        <div className="ent-panel overflow-x-auto" data-testid="insp-report-recon">
          <div className="ent-colhead"><span className="ent-coltitle flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5" /> Recon</span><span className="ent-pill">{formatCurrency(data.recon_cost || 0)}</span></div>
          <table className="ent-table">
            <thead><tr><th>Problema</th><th>Ação</th><th>Prioridade</th><th>Status</th><th className="text-right">Custo</th></tr></thead>
            <tbody>
              {recon.map((r) => (
                <tr key={r.id}>
                  <td className="font-medium">{r.issue}</td>
                  <td className="text-foreground/75">{r.action || '—'}</td>
                  <td><span className={`ent-tag ${r.priority === 'Critical' ? 't-bad' : r.priority === 'High' ? 't-warn' : ''}`}>{r.priority}</span></td>
                  <td><span className={`ent-status ${r.status === 'Completed' ? 't-ok' : r.status === 'Cancelled' ? '' : 't-warn'}`}><span className="ent-dot" />{r.status}</span></td>
                  <td className="num text-right">{formatCurrency(r.totals?.total || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="ent-panel p-3">
        <p className="ent-label flex items-center gap-1.5 mb-2"><Images className="w-3.5 h-3.5" /> Fotos da inspeção ({photos.length})</p>
        {photos.length ? (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="insp-report-photos">
            {photos.map((p) => (
              <a key={p.id} href={`${API}/files/${p.path}`} target="_blank" rel="noreferrer" className="relative block group">
                <img src={`${API}/files/${p.path}`} alt={p.kind} className="w-full aspect-square object-cover rounded border border-border group-hover:border-primary transition-colors" />
                <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-[8px] px-1 truncate uppercase">{p.item_key || p.kind}</span>
              </a>
            ))}
          </div>
        ) : <p className="text-[12px] t-label">Nenhuma foto enviada.</p>}
      </div>

      <div className="flex items-center gap-2 text-[11px] t-label flex-wrap">
        <ClipboardCheck className="w-3 h-3" />
        <span>Inspeção criada por {i.created_by} · {(i.created_at || '').slice(0, 10)}</span>
        {i.completed_at && <span>· Finalizada por {i.completed_by} em {(i.completed_at || '').slice(0, 10)}</span>}
        {i.qc_by && <span>· QC por {i.qc_by}</span>}
      </div>
    </div>
  );
};

export default InspectionReport;
